import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ShieldAlert,
  ArrowLeft,
  Trash2,
  Edit3,
  Archive,
  RefreshCw,
  Plus,
  Play,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileCode,
  Layers,
  Sparkles,
} from "lucide-react";
import {
  guardrailsGetItem,
  guardrailsUpdateItem,
  guardrailsDeleteItem,
  guardrailsBatchAnchors,
  guardrailsValidate,
} from "../gen";
import type {
  GuardrailItemDetailResponse,
  GuardrailValidateResponse,
} from "../gen/models";
import { useToast } from "../context/ToastContext";
import { useAgentOptions } from "../hooks/useAgentOptions";
import { ConfirmModal } from "../components/ConfirmModal";
import { EditGuardrailModal } from "../components/guardrails/EditGuardrailModal";
import { TimeAgo } from "../components/common/TimeAgo";
import { normalizeAgentType, sanitizeProtocoloName } from "../hooks/useUserDirectory";

export function GuardrailDetail() {
  const { item_id } = useParams<{ item_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const agentOptions = useAgentOptions({ includeUniversal: true });

  const [item, setItem] = useState<GuardrailItemDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Active Tab
  const [activeTab, setActiveTab] = useState<"policy" | "anchors" | "tester">("policy");

  // Edit Modal
  const [isEditing, setIsEditing] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Delete Confirm Modal
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Add Anchors State
  const [newAnchorType, setNewAnchorType] = useState<string>("negative");
  const [newAnchorsText, setNewAnchorsText] = useState("");
  const [addingAnchors, setAddingAnchors] = useState(false);

  // Live Sandbox State
  const [testQuery, setTestQuery] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<GuardrailValidateResponse | null>(null);

  const getAgentLabel = (agentType?: string | null) => {
    if (!agentType || agentType === "universal") {
      return "Universal (All Agents)";
    }
    const norm = normalizeAgentType(agentType);
    const match = agentOptions.find((a) => a.value === norm || a.value === agentType);
    return match?.label || sanitizeProtocoloName(norm, "Sofia");
  };

  const loadItem = async () => {
    if (!item_id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await guardrailsGetItem(item_id);
      const data = (res as any).data as GuardrailItemDetailResponse;
      setItem(data);
    } catch (err: any) {
      setError(err?.message || "Failed to load guardrail policy");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItem();
    setTestResult(null);
  }, [item_id]);

  const handleUpdate = async (payload: any) => {
    if (!item_id) return;
    setEditSubmitting(true);
    try {
      const res = await guardrailsUpdateItem(item_id, payload);
      setItem((res as any).data);
      setIsEditing(false);
      toast.success("Guardrail policy updated successfully.");
    } catch (err: any) {
      toast.error("Failed to update guardrail: " + err.message);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!item_id) return;
    setDeleteSubmitting(true);
    try {
      await guardrailsDeleteItem(item_id);
      toast.success("Guardrail policy deleted.");
      setIsDeleting(false);
      navigate(`/guardrails${location.search}`);
    } catch (err: any) {
      toast.error("Failed to delete guardrail: " + err.message);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!item || !item_id) return;
    const nextStatus = item.status === "active" ? "archived" : "active";
    try {
      const res = await guardrailsUpdateItem(item_id, { status: nextStatus });
      setItem((res as any).data);
      toast.success(`Guardrail policy marked as ${nextStatus}.`);
    } catch (err: any) {
      toast.error("Failed to toggle status: " + err.message);
    }
  };

  const handleAddAnchors = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item_id || !newAnchorsText.trim()) return;

    const rawLines = newAnchorsText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    if (rawLines.length === 0) return;

    setAddingAnchors(true);
    try {
      await guardrailsBatchAnchors({
        item_id: item_id,
        anchors: rawLines.map((text) => ({
          anchor_type: newAnchorType,
          text,
        })),
      });
      setNewAnchorsText("");
      toast.success(`Added and indexed ${rawLines.length} anchor(s).`);
      await loadItem();
    } catch (err: any) {
      toast.error("Failed to ingest anchors: " + err.message);
    } finally {
      setAddingAnchors(false);
    }
  };

  const handleRunTest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testQuery.trim() || !item) return;

    setTesting(true);
    setTestResult(null);
    try {
      const res = await guardrailsValidate({
        agent_type: item.agent_type || "frontdesk",
        text: testQuery.trim(),
      });
      setTestResult((res as any).data as GuardrailValidateResponse);
    } catch (err: any) {
      toast.error("Failed to validate query: " + err.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-stone-400 dark:text-stone-500 text-xs gap-2">
        <RefreshCw className="w-4 h-4 animate-spin text-stone-500" />
        <span>Loading guardrail details...</span>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-3">
        <ShieldAlert className="w-10 h-10 text-rose-400" />
        <div className="text-sm font-semibold text-stone-800 dark:text-stone-200">
          {error || "Guardrail policy not found"}
        </div>
        <button
          onClick={() => navigate(`/guardrails${location.search}`)}
          className="px-3 py-1.5 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800 text-xs text-stone-700 dark:text-stone-300 hover:bg-stone-100"
        >
          Return to Guardrails
        </button>
      </div>
    );
  }

  const meta = (item.metadata || {}) as Record<string, any>;
  const anchors = item.anchors || [];

  return (
    <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden bg-white dark:bg-stone-900 select-none">
      {/* Pane Header */}
      <div className="p-4 border-b border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40 shrink-0 space-y-3">
        <div className="md:hidden pb-1">
          <button
            type="button"
            onClick={() => navigate(`/guardrails${location.search}`)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Back to guardrails</span>
          </button>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 rounded text-[10px] uppercase font-mono font-bold bg-stone-200 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-300 dark:border-stone-700">
                {item.category}
              </span>

              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${
                  item.action === "block"
                    ? "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-400 dark:border-rose-900"
                    : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-900"
                }`}
              >
                {item.action === "block" ? "Hard Block" : "Soft Flag"}
              </span>

              <span
                className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase border ${
                  item.severity === "critical"
                    ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/60 dark:text-red-400 dark:border-red-900"
                    : item.severity === "high"
                    ? "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/60 dark:text-orange-400 dark:border-orange-900"
                    : "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700"
                }`}
              >
                {item.severity}
              </span>

              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize border ${
                  item.status === "active"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800"
                    : "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700"
                }`}
              >
                {item.status}
              </span>

              <span className="text-[10px] font-mono text-stone-400 dark:text-stone-500">
                Scope: {getAgentLabel(item.agent_type)}
              </span>
            </div>

            <h1 className="text-base font-bold text-stone-900 dark:text-stone-100 tracking-tight">
              {item.title}
            </h1>
            {item.description && (
              <p className="text-xs text-stone-600 dark:text-stone-400 line-clamp-2">
                {item.description}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={handleToggleStatus}
              className="p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
              title={item.status === "active" ? "Archive guardrail" : "Activate guardrail"}
            >
              <Archive className="w-3.5 h-3.5" />
            </button>

            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 text-xs font-semibold hover:bg-stone-100 dark:hover:bg-stone-800 transition"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>

            <button
              type="button"
              onClick={() => setIsDeleting(true)}
              className="p-1.5 rounded-lg border border-rose-200 dark:border-rose-900 bg-rose-50/50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900 transition"
              title="Delete guardrail policy"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Tab Selector */}
        <div className="flex items-center gap-2 border-t border-stone-200/80 dark:border-stone-800/80 pt-2.5">
          <button
            type="button"
            onClick={() => setActiveTab("policy")}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${
              activeTab === "policy"
                ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 shadow-2xs"
                : "text-stone-600 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/60"
            }`}
          >
            Policy & Rules
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("anchors")}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 ${
              activeTab === "anchors"
                ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 shadow-2xs"
                : "text-stone-600 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/60"
            }`}
          >
            <span>Vector Anchors</span>
            <span className="px-1.5 py-0.2 rounded-full bg-stone-200/70 dark:bg-stone-800 text-[10px] font-mono">
              {anchors.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("tester")}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 ${
              activeTab === "tester"
                ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 shadow-2xs"
                : "text-stone-600 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800/60"
            }`}
          >
            <Sparkles className="w-3 h-3 text-amber-500" />
            <span>Live Tester</span>
          </button>
        </div>
      </div>

      {/* Tab Body */}
      <div className="flex-1 overflow-y-auto p-5 select-text">
        {activeTab === "policy" && (
          <div className="space-y-6 max-w-3xl">
            {/* Behavior Card */}
            <div
              className={`p-4 rounded-xl border ${
                item.action === "block"
                  ? "bg-rose-50/40 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900"
                  : "bg-amber-50/40 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900"
              } space-y-3`}
            >
              <div className="flex items-center gap-2">
                {item.action === "block" ? (
                  <ShieldAlert className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                )}
                <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100">
                  {item.action === "block"
                    ? "Hard Block Enforcement Strategy"
                    : "Soft Flag Boundary Strategy"}
                </h3>
              </div>

              <p className="text-xs text-stone-700 dark:text-stone-300 leading-relaxed">
                {item.action === "block"
                  ? "Inbound messages matching negative signature anchors within the cosine threshold trigger an immediate hard block. Downstream LLM reasoning and tools are completely bypassed to preserve security and eliminate token spend. An attack alert card with zero buttons is dispatched to Slack."
                  : "Inbound messages touching this boundary trigger an advisory flag. The concierge draft is generated with contextual advisory guidance injected into the system prompt, but direct sending is blocked. Only 'Alter & Send' is permitted after human review."}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2 border-t border-stone-200/60 dark:border-stone-800/60 text-xs">
                <div>
                  <span className="text-[10px] text-stone-500 block uppercase font-mono">
                    Cosine Threshold
                  </span>
                  <span className="font-mono font-bold text-stone-900 dark:text-stone-100">
                    {item.default_threshold.toFixed(3)}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] text-stone-500 block uppercase font-mono">
                    On-Block Action
                  </span>
                  <span className="font-semibold text-stone-900 dark:text-stone-100 capitalize">
                    {meta.on_block_action || "silent"}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] text-stone-500 block uppercase font-mono">
                    Total Anchors
                  </span>
                  <span className="font-mono font-bold text-stone-900 dark:text-stone-100">
                    {anchors.length}
                  </span>
                </div>
              </div>

              {meta.on_block_action === "canned_response" && meta.canned_response_text && (
                <div className="pt-2 border-t border-stone-200/60 dark:border-stone-800/60">
                  <span className="text-[10px] text-stone-500 block uppercase font-mono">
                    Canned Refusal Response
                  </span>
                  <div className="mt-1 p-2.5 rounded-lg bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 text-xs text-stone-800 dark:text-stone-200 italic">
                    "{meta.canned_response_text}"
                  </div>
                </div>
              )}

              {item.action === "flag" && meta.advisory && (
                <div className="pt-2 border-t border-stone-200/60 dark:border-stone-800/60">
                  <span className="text-[10px] text-stone-500 block uppercase font-mono">
                    Injected Prompt Advisory
                  </span>
                  <div className="mt-1 p-2.5 rounded-lg bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 text-xs text-stone-800 dark:text-stone-200 font-mono">
                    {meta.advisory}
                  </div>
                </div>
              )}
            </div>

            {/* Metadata Inspector */}
            <div className="p-4 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40 space-y-2">
              <div className="flex items-center gap-2">
                <FileCode className="w-4 h-4 text-stone-500" />
                <h4 className="text-xs font-bold text-stone-900 dark:text-stone-100">
                  Policy Metadata Payload
                </h4>
              </div>
              <pre className="p-3 rounded-lg bg-stone-100 dark:bg-stone-950 text-[11px] font-mono text-stone-800 dark:text-stone-200 overflow-x-auto border border-stone-200 dark:border-stone-800">
                {JSON.stringify(
                  {
                    id: item.id,
                    org_id: item.org_id,
                    agent_type: item.agent_type,
                    category: item.category,
                    action: item.action,
                    severity: item.severity,
                    default_threshold: item.default_threshold,
                    created_at: item.created_at,
                    updated_at: item.updated_at,
                    ...meta,
                  },
                  null,
                  2
                )}
              </pre>
            </div>
          </div>
        )}

        {activeTab === "anchors" && (
          <div className="space-y-6 max-w-3xl">
            {/* Add Anchors Box */}
            <form
              onSubmit={handleAddAnchors}
              className="p-4 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40 space-y-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Plus className="w-4 h-4 text-stone-600 dark:text-stone-300" />
                  <h4 className="text-xs font-bold text-stone-900 dark:text-stone-100">
                    Batch Ingest Signature Anchors
                  </h4>
                </div>
                <select
                  value={newAnchorType}
                  onChange={(e) => setNewAnchorType(e.target.value)}
                  className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2.5 py-1 text-xs text-stone-900 dark:text-stone-100"
                >
                  <option value="negative">Negative Anchor (Attack Pattern)</option>
                  <option value="positive">Positive Anchor (Allowed Topic)</option>
                </select>
              </div>

              <textarea
                rows={3}
                value={newAnchorsText}
                onChange={(e) => setNewAnchorsText(e.target.value)}
                placeholder="Enter new signature anchors (one per line)..."
                className="w-full font-mono text-xs rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2.5 text-stone-900 dark:text-stone-100 focus:border-stone-500 focus:outline-hidden"
              />

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={addingAnchors || !newAnchorsText.trim()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition disabled:opacity-50"
                >
                  {addingAnchors ? "Embedding..." : "Add & Embed Anchors"}
                </button>
              </div>
            </form>

            {/* Anchors List */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-stone-500 px-1">
                <span>Indexed Vector Signatures ({anchors.length})</span>
                <span className="font-mono text-[10px]">FastEmbed all-MiniLM-L6-v2 (384-dim)</span>
              </div>

              {anchors.length === 0 ? (
                <div className="p-8 text-center border border-dashed border-stone-200 dark:border-stone-800 rounded-xl space-y-2">
                  <Layers className="w-8 h-8 text-stone-300 dark:text-stone-600 mx-auto" />
                  <div className="text-xs font-semibold text-stone-700 dark:text-stone-300">
                    No anchors registered
                  </div>
                  <p className="text-[11px] text-stone-400">
                    Add signature anchors above to enable vector similarity evaluation.
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {anchors.map((a, idx) => (
                    <div
                      key={a.id || idx}
                      className="p-3 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 flex items-start justify-between gap-3 text-xs"
                    >
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-1.5 py-0.2 rounded text-[9px] uppercase font-mono font-bold border ${
                              a.anchor_type === "positive"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-400"
                                : "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950 dark:text-rose-400"
                            }`}
                          >
                            {a.anchor_type}
                          </span>
                          <span className="text-[10px] text-stone-400 font-mono">
                            <TimeAgo date={a.created_at} />
                          </span>
                        </div>
                        <p className="font-mono text-[11px] text-stone-800 dark:text-stone-200 break-words">
                          {a.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "tester" && (
          <div className="space-y-5 max-w-3xl">
            <form
              onSubmit={handleRunTest}
              className="p-4 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40 space-y-3"
            >
              <div>
                <h4 className="text-xs font-bold text-stone-900 dark:text-stone-100">
                  Live Guardrail Simulation Sandbox
                </h4>
                <p className="text-[11px] text-stone-500 dark:text-stone-400">
                  Test arbitrary guest messages against pgvector HNSW dual-manifold barriers in real time
                </p>
              </div>

              <textarea
                rows={3}
                value={testQuery}
                onChange={(e) => setTestQuery(e.target.value)}
                placeholder="Enter guest message, prompt injection attack, or off-topic test inquiry..."
                className="w-full text-xs rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2.5 text-stone-900 dark:text-stone-100 focus:border-stone-500 focus:outline-hidden"
              />

              <div className="flex items-center justify-between">
                <span className="text-[11px] text-stone-400">
                  Target Agent: <strong className="text-stone-700 dark:text-stone-300">{normalizeAgentType(item.agent_type || "frontdesk")}</strong>
                </span>
                <button
                  type="submit"
                  disabled={testing || !testQuery.trim()}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition disabled:opacity-50 shadow-xs"
                >
                  <Play className={`w-3.5 h-3.5 ${testing ? "animate-spin" : ""}`} />
                  <span>{testing ? "Evaluating..." : "Run Guardrail Check"}</span>
                </button>
              </div>
            </form>

            {testResult && (
              <div
                className={`p-4 rounded-xl border animate-in fade-in duration-150 space-y-3 ${
                  testResult.action === "block"
                    ? "bg-rose-50/40 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900"
                    : testResult.action === "flag"
                    ? "bg-amber-50/40 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900"
                    : "bg-emerald-50/40 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {testResult.action === "block" ? (
                      <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                    ) : testResult.action === "flag" ? (
                      <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    ) : (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    )}
                    <h4 className="text-sm font-bold capitalize text-stone-900 dark:text-stone-100">
                      Evaluation Outcome: {testResult.action.toUpperCase()}
                    </h4>
                  </div>
                  <span className="text-[10px] font-mono text-stone-500">
                    Latency: {testResult.evaluation_time_ms.toFixed(1)} ms
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3 p-3 rounded-lg bg-white dark:bg-stone-900 border border-stone-200/80 dark:border-stone-800 text-xs">
                  <div>
                    <span className="text-[10px] text-stone-400 uppercase font-mono block">
                      Attack Similarity
                    </span>
                    <span className="font-mono font-bold text-stone-900 dark:text-stone-100">
                      {testResult.similarity_score.toFixed(4)}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] text-stone-400 uppercase font-mono block">
                      Hospitality Domain Sim
                    </span>
                    <span className="font-mono font-bold text-stone-900 dark:text-stone-100">
                      {testResult.positive_domain_similarity.toFixed(4)}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] text-stone-400 uppercase font-mono block">
                      Topological Margin (Δ)
                    </span>
                    <span className="font-mono font-bold text-stone-900 dark:text-stone-100">
                      {testResult.topological_margin.toFixed(4)}
                    </span>
                  </div>
                </div>

                {testResult.anchor_text && (
                  <div className="text-xs space-y-1">
                    <span className="text-[10px] text-stone-400 uppercase font-mono block">
                      Closest Signature Anchor
                    </span>
                    <p className="font-mono text-[11px] p-2 rounded-md bg-stone-100 dark:bg-stone-950 text-stone-800 dark:text-stone-200">
                      {testResult.anchor_text}
                    </p>
                  </div>
                )}

                {testResult.canned_response_text && (
                  <div className="text-xs space-y-1">
                    <span className="text-[10px] text-stone-400 uppercase font-mono block">
                      Guest Canned Response
                    </span>
                    <p className="p-2 rounded-md bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 text-stone-800 dark:text-stone-200 italic">
                      "{testResult.canned_response_text}"
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <EditGuardrailModal
        isOpen={isEditing}
        item={item}
        onClose={() => setIsEditing(false)}
        onSubmit={handleUpdate}
        submitting={editSubmitting}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleting}
        onClose={() => setIsDeleting(false)}
        onConfirm={handleDelete}
        title="Delete Guardrail Policy"
        description={`Are you sure you want to delete policy "${item.title}"? All associated signature vector anchors will be permanently purged from the database.`}
        confirmText="Delete Policy"
        variant="danger"
        isLoading={deleteSubmitting}
        loadingText="Purging policy..."
      />
    </div>
  );
}
